import { forwardRef, useRef, useEffect } from "react";
import { v4 } from "uuid";
import IMask from "imask";

/**
 * A wrapper around the gds-input-field web component that is compatible with react-hook-forms.
 * @deprecated Use InputBox instead.
 */
const Input = forwardRef(function Input(props, ref) {
    const {
        name,
        onChange,
        onBlur,
        children,
        disabled,
        mask,
        ...rest
    } = props;

    const gdsInputFieldIdRef = useRef(`input-${v4()}`);

    useEffect(() => {
        const setupInputElement = () => {
            const gdsInputField = document.getElementById(gdsInputFieldIdRef.current);
            const htmlInputElement = gdsInputField?.shadowRoot?.querySelector("input");

            /*
            The form library used by this project, react-hook-forms, requires a ref to the HTMLInputElement
            and access to its onChange and onBlur events. The GDS web component doesn't provide us access
            to these things, so we have to break through the Shadow DOM encapsulation.
            */
            if (htmlInputElement) {
                ref?.(htmlInputElement);
                htmlInputElement.onchange = onChange ?? htmlInputElement.onchange;
                htmlInputElement.onblur = onBlur ?? htmlInputElement.onblur;
                htmlInputElement.name = name ?? htmlInputElement.name;

                /*
                IMask is used internally by the <gds-input-field> for phone number masking. By adding this
                here, we can use whatever mask we want.
                */
                if (mask) {
                    new IMask(htmlInputElement, { mask: mask });
                }

                /*
                The <gds-input-field> tracks the input value internally with a private variable that is set
                on the input event. We need to dispatch an input event here so that it picks up the default 
                value set by react-hook-forms.
                */
                htmlInputElement.dispatchEvent(new Event('input', { bubbles: true }));
                return true; // Setup completed successfully
            }

            return false; // Setup failed, shadowRoot not ready
        };

        // Try to setup immediately first
        if (setupInputElement()) {
            return;
        }

        // If immediate setup fails, use MutationObserver to wait for shadowRoot
        const gdsInputField = document.getElementById(gdsInputFieldIdRef.current);
        if (gdsInputField) {
            const observer = new MutationObserver((mutations) => {
                // Check if shadowRoot has been created
                if (gdsInputField.shadowRoot) {
                    if (setupInputElement()) {
                        observer.disconnect();
                    }
                }
            });

            // Start observing the gds-input-field element for changes
            observer.observe(gdsInputField, {
                childList: true,
                subtree: true,
                attributes: true
            });

            // Cleanup observer on component unmount
            return () => observer.disconnect();
        }
    }, [ref, onChange, onBlur, name, mask]);

    return <gds-input-field
        id={gdsInputFieldIdRef.current}
        disabled={disabled ? "disabled" : null}
        {...rest}
    >
        {children}
    </gds-input-field>
});

export default Input;